import clsx from 'clsx'
import DateTimePicker from 'components/DateTimePicker'
import FormField from 'components/FormField'
import dayjs from 'dayjs'
import { observer } from 'mobx-react'
import { Dropdown } from 'primereact/dropdown'
import InputNumberCustom from 'components/InputNumberCustom'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DATE_TIME_FORMAT } from 'utils/constants/datetime'
import { EventApplicationUnitEnum } from 'utils/constants/event'
import { useParams } from 'react-router'

function getRegistrationBeforeMaxMs(values: {
  startTimeEvent?: Date | null
  timeslots?: { timeRange?: { from?: string | null } }[]
}): number | null {
  const startTimeEvent = values.startTimeEvent
  const timeslots = values.timeslots
  if (!startTimeEvent || !timeslots?.length) return null
  const lastFrom = timeslots[timeslots.length - 1]?.timeRange?.from
  if (!lastFrom) return null
  const dateOnly = dayjs(startTimeEvent).format(DATE_TIME_FORMAT.DAY_ONLY)
  return dayjs(`${dateOnly} ${lastFrom}`, DATE_TIME_FORMAT.FULL_SECOND)
    .add(15, 'minute')
    .valueOf()
}

function getEventFirstSlotStartMs(values: {
  startTimeEvent?: Date | null
  timeslots?: { timeRange?: { from?: string | null } }[]
}): number | null {
  const startTimeEvent = values.startTimeEvent
  const firstFrom = values.timeslots?.[0]?.timeRange?.from
  if (!startTimeEvent || !firstFrom) return null
  const dateOnly = dayjs(startTimeEvent).format(DATE_TIME_FORMAT.DAY_ONLY)
  const ms = dayjs(
    `${dateOnly} ${firstFrom}`,
    DATE_TIME_FORMAT.FULL_SECOND
  ).valueOf()
  return Number.isFinite(ms) ? ms : null
}

const RegistorInformation = ({ disabled }: { disabled?: boolean }) => {
  const { t } = useTranslation()

  const { control, getValues } = useFormContext()
  const { id } = useParams()
  const disabledAll = !!id

  return (
    <>
      <FormField
        control={control}
        name="receptionBefore"
        className="w-6 pr-2"
        rules={{
          required: t('required', {
            field: t('Registration Before'),
          }),
          validate: (val) => {
            if (!val) return null
            if (!disabledAll && dayjs(val).valueOf() <= Date.now()) {
              return t('Reception must greater than current')
            }
            const maxMs = getRegistrationBeforeMaxMs(getValues())
            if (maxMs == null) return null
            if (dayjs(val).valueOf() > maxMs)
              return t(
                'Registration Before time must be less than or equal to last timeslot start time + 15 minutes'
              )
            return null
          },
        }}
        render={({ field, fieldState }) => {
          return (
            <DateTimePicker
              minDate={new Date()}
              {...field}
              onChange={(date) => {
                field.onChange(date)
              }}
              tabIndex={100}
              disabled={disabled}
              placeholder={t('Select')}
              className={clsx('w-full', {
                'p-invalid': fieldState.invalid,
              })}
            />
          )
        }}
        label={t('Registration Before')}
      />

      <FormField
        control={control}
        className="w-6 pr-2"
        name="cancelBefore"
        rules={{
          required: t('required', {
            field: t('Cancel Before'),
          }),
          validate: (val) => {
            if (!val) return null
            if (!disabledAll && dayjs(val).valueOf() <= Date.now()) {
              return t('Cancel before time must greater than current')
            }
            const values = getValues()
            const firstSlotMs = getEventFirstSlotStartMs(values)
            if (firstSlotMs == null) return null

            const deadlineMs = firstSlotMs - 60 * 60 * 1000
            const valMs = dayjs(val).valueOf()

            if (valMs > deadlineMs) {
              return t(
                'Cancel before time must be less than or equal to event start time - 1 hour'
              )
            }
            return null
          },
        }}
        render={({ field, fieldState }) => {
          return (
            <DateTimePicker
              {...field}
              disabled={disabled}
              minDate={new Date()}
              tabIndex={101}
              placeholder={t('Select')}
              className={clsx('', {
                'p-invalid': fieldState.invalid,
              })}
            />
          )
        }}
        label={t('Cancel Before')}
      />
      <div className="w-6 pr-2">
        <FormField
          control={control}
          name="qrValidUntil"
          rules={{
            validate: (val) => {
              const startTimeEvent = getValues().startTimeEvent
              const timeslots = getValues().timeslots
              if (!val || !startTimeEvent || !timeslots?.length) return null

              const lastSlotTo = timeslots[timeslots.length - 1]?.timeRange?.to
              if (!lastSlotTo) return null

              const dateOnly = dayjs(startTimeEvent).format(
                DATE_TIME_FORMAT.DAY_ONLY
              )
              const qrValidUntilMs = dayjs(val).valueOf()
              const lastSlotEndMs = dayjs(
                `${dateOnly} ${lastSlotTo}`,
                DATE_TIME_FORMAT.FULL_SECOND
              ).valueOf()
              const maxMs = dayjs(lastSlotEndMs).add(1, 'hour').valueOf()

              if (qrValidUntilMs < lastSlotEndMs)
                return t(
                  'QR Code expiration time must be greater than or equal to the end time of the last time slot'
                )
              if (qrValidUntilMs > maxMs)
                return t(
                  'QR Code expiration time must be less than or equal to end time of last time slot + 1 hour'
                )
              return null
            },
          }}
          render={({ field, fieldState }) => {
            return (
              <DateTimePicker
                {...field}
                disabled={disabled}
                minDate={new Date()}
                tabIndex={102}
                placeholder={t('Select')}
                showClear
                className={clsx('', {
                  'p-invalid': fieldState.invalid,
                })}
              />
            )
          }}
          label={t('QR Code Expiration')}
          requiredMessage={t(
            'QRコードがこの時刻で無効になります。デフォルトは予約の最終時間枠の終了時刻です'
          )}
        />
      </div>
      <FormField
        control={control}
        rules={{
          required: t('required', {
            field: t('Remind Before'),
          }),
          validate: (val) => {
            const startTime = getValues().startTimeEvent
            const firstSlot = getValues().timeslots?.[0]?.timeRange?.from

            if (val < 0) return t('Remind Before must be >= 0')

            if (startTime && firstSlot) {
              if (
                dayjs(
                  `${dayjs(startTime).format(DATE_TIME_FORMAT.DAY_ONLY)} ${firstSlot}`,
                  DATE_TIME_FORMAT.FULL_SECOND
                ).valueOf() -
                  val * 3600000 <
                  new Date().getTime() &&
                !disabledAll
              )
                return t('The remind time must be after the current time.')
            }
            return null
          },
        }}
        name="remindBefore"
        className="w-6 pr-2"
        render={({ field, fieldState }) => {
          return (
            <div className="relative">
              <InputNumberCustom
                {...field}
                onChange={({ value }) => {
                  field.onChange(value)
                }}
                disabled={disabledAll}
                maxLength={6}
                placeholder={t('Select')}
                className={clsx('', {
                  'p-invalid': fieldState.invalid,
                })}
              />

              <span
                className="absolute text-gray-400"
                style={{
                  right: 8,
                  top: 12,
                }}
              >
                {t('hours')}
              </span>
            </div>
          )
        }}
        label={t('Remind Before')}
      />

      <FormField
        control={control}
        name="applicationUnit"
        rules={{
          required: t('required', { field: t('Applications Unit') }),
        }}
        className="w-6 pr-2"
        render={({ field, fieldState }) => {
          return (
            <Dropdown
              disabled={disabledAll}
              {...field}
              placeholder={t('Select')}
              options={Object.values(EventApplicationUnitEnum).map((key) => ({
                label: t(key),
                value: key,
              }))}
              className={clsx({
                'p-invalid': fieldState.invalid,
              })}
            />
          )
        }}
        label={t('Applications Unit')}
      />
    </>
  )
}
export default observer(RegistorInformation)
